import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Send, Eye, RefreshCw, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PreviewModal = ({ guide, authHeaders, onClose, onSent }) => {
  const [preview, setPreview] = useState(null);
  const [includeAll, setIncludeAll] = useState(false);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    axios.get(`${API}/admin/guide-editions/${guide.product_id}/preview`, authHeaders).then((r) => setPreview(r.data))
      .catch((e) => { toast.error(e?.response?.data?.detail || 'Could not load preview'); onClose(); });
    // eslint-disable-next-line
  }, [guide.product_id]);
  const pending = preview ? (includeAll ? preview.buyers : preview.buyers - preview.notified_this_edition) : 0;
  const send = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/guide-editions/${guide.product_id}/notify`, { include_already_notified: includeAll }, authHeaders);
      toast.success(`Sending Edition ${r.data.edition} update to ${pending} buyer${pending === 1 ? '' : 's'}`);
      onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send update'); }
    setSending(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="edition-preview-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg font-semibold text-[#1E1622]">Notify buyers: {guide.town} Edition {guide.edition}</h3>
          <button onClick={onClose} data-testid="edition-preview-close"><X size={20} /></button>
        </div>
        {!preview ? <Loader2 className="animate-spin text-[#3B164C]" /> : (
          <>
            <p className="text-sm text-[#1E1622]/70 mb-1"><span className="font-medium">Subject:</span> {preview.subject}</p>
            <p className="text-xs text-[#1E1622]/50 mb-4">Each buyer gets their own fresh links and PDF password; the preview uses placeholders.</p>
            <iframe title="Email preview" srcDoc={preview.html} sandbox="" className="w-full h-80 border border-[#E6DAC5] rounded-xl mb-4" data-testid="edition-preview-frame" />
            <label className="flex items-center gap-2 text-sm text-[#1E1622]/70 mb-4">
              <input type="checkbox" checked={includeAll} onChange={(e) => setIncludeAll(e.target.checked)} data-testid="edition-include-all" />
              Also resend to buyers already notified about this edition ({preview.notified_this_edition})
            </label>
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#1E1622]/70" data-testid="edition-recipient-count">{pending} of {preview.buyers} buyers will receive this email</p>
              <button onClick={send} disabled={sending || pending <= 0} data-testid="edition-send-btn" className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send update
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const TownAlertsPanel = ({ authHeaders }) => {
  const [data, setData] = useState(null);
  // eslint-disable-next-line
  useEffect(() => { axios.get(`${API}/admin/town-alerts`, authHeaders).then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  const exportCsv = () => {
    const rows = [['email', 'regions', 'any_new_town', 'signed_up'], ...data.subscribers.map((s) => [s.email, (s.regions || []).join('; '), s.any_town ? 'yes' : 'no', s.created_at || ''])];
    const blob = new Blob([rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'town-alert-subscribers.csv'; a.click();
  };
  return (
    <div className="bg-white rounded-2xl border border-[#E6DAC5] p-6" data-testid="town-alerts-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif text-lg font-semibold text-[#1E1622]">New-town alert subscribers <span className="text-[#C5A059]" data-testid="town-alerts-total">{data.subscribers.length}</span></h3>
        {data.subscribers.length > 0 && <button onClick={exportCsv} data-testid="town-alerts-export" className="btn-secondary text-xs">Export CSV</button>}
      </div>
      <p className="text-sm text-[#1E1622]/60 mb-4">When the 6-hourly check finds a newly published town, subscribers for its region (and "Any new town") get one email.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        <span className="px-3 py-1 rounded-full bg-[#3B164C] text-[#FAF7F2] text-xs">Any new town · {data.any_town}</span>
        {Object.entries(data.by_region).sort().map(([r, n]) => <span key={r} className="px-3 py-1 rounded-full bg-[#F5EFE9] text-[#1E1622] text-xs">{r} · {n}</span>)}
      </div>
      {data.sent.length > 0 && (
        <div className="text-xs text-[#1E1622]/60 space-y-1" data-testid="town-alerts-sent">
          {data.sent.map((x) => <p key={x.product_id}>{x.slug}: {x.sent ?? 0} of {x.recipients ?? 0} emailed · {new Date(x.started_at).toLocaleDateString('en-AU')}</p>)}
        </div>
      )}
    </div>
  );
};

export const GuideEditionsTab = ({ authHeaders }) => {
  const [guides, setGuides] = useState(null);
  const [active, setActive] = useState(null);
  const [error, setError] = useState('');
  const load = () => axios.get(`${API}/admin/guide-editions`, authHeaders)
    .then((r) => { setGuides(r.data.guides || []); setError(''); })
    .catch((e) => { setGuides([]); setError(e?.response?.data?.detail || 'Could not load guides'); });
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-6" data-testid="guide-editions-section">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl font-semibold text-[#1E1622]">Guide Editions</h2>
        <button onClick={() => { setGuides(null); load(); }} data-testid="guide-editions-refresh" className="btn-secondary text-sm inline-flex items-center gap-2"><RefreshCw size={14} /> Refresh</button>
      </div>
      <p className="text-sm text-[#1E1622]/60">Every 6 hours we check the studio for new editions and automatically email past buyers (including pack buyers) fresh links to the updated guide. You can also send an update manually.</p>
      {!guides ? <Loader2 className="animate-spin text-[#3B164C]" /> : (
        <div className="bg-white rounded-2xl border border-[#E6DAC5] overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#F5EFE9]"><tr>
              {['Guide', 'Edition', 'Buyers', 'Notified', ''].map((h) => <th key={h} className="text-left p-4 text-sm font-semibold text-[#1E1622]">{h}</th>)}
            </tr></thead>
            <tbody>
              {guides.map((g) => (
                <tr key={g.product_id} className="border-t border-[#E6DAC5]" data-testid={`edition-row-${g.slug}`}>
                  <td className="p-4"><div className="flex items-center gap-3">
                    <img src={g.cover_image_url} alt={g.town} className="w-9 h-11 rounded object-cover" />
                    <div><p className="text-sm font-medium text-[#1E1622]">{g.town}</p><p className="text-xs text-[#1E1622]/50">{g.region}</p></div>
                  </div></td>
                  <td className="p-4 text-sm text-[#3B164C] font-semibold">Ed. {g.edition}</td>
                  <td className="p-4 text-sm">{g.buyers}</td>
                  <td className="p-4 text-xs text-[#1E1622]/60">{g.notified_this_edition} for this edition{g.last_notified_at && <><br />Last run {new Date(g.last_notified_at).toLocaleDateString('en-AU')}</>}</td>
                  <td className="p-4 text-right">
                    <button onClick={() => setActive(g)} data-testid={`edition-notify-${g.slug}`} className="text-xs text-[#3B164C] hover:text-[#C5A059] inline-flex items-center gap-1"><Eye size={12} /> Preview &amp; notify</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {guides.length === 0 && <p className="p-12 text-center text-[#1E1622]/50" data-testid="guide-editions-empty">{error || 'No guides in the studio feed'}</p>}
        </div>
      )}
      <TownAlertsPanel authHeaders={authHeaders} />
      {active && <PreviewModal guide={active} authHeaders={authHeaders} onClose={() => setActive(null)} onSent={() => { setActive(null); setTimeout(load, 3000); }} />}
    </div>
  );
};
